import type { DisplayState, EventMsg, ServerMessage, WeatherSlice } from '../protocol/types';

export type ConnectionStatus =
  | 'idle'
  | 'connecting' // richiesta ticket / apertura socket
  | 'live' // welcome ricevuto, dati in arrivo
  | 'reconnecting' // socket perso, backoff in corso
  | 'polling' // WS non disponibile, fallback REST attivo
  | 'unauthorized'; // device key revocata: serve intervento dal portale

export interface StoreState {
  data: Partial<DisplayState>;
  connection: {
    status: ConnectionStatus;
    attempts: number;
    lastMessageAt: number | null;
    lastError: string | null;
  };
  /** serverTime - Date.now(): sommato all'ora locale per un orologio allineato al server. */
  clockOffsetMs: number;
  /** Ultimi eventi ricevuti (max 20), per animazioni o ticker. */
  events: EventMsg['payload'][];
  lastSeq: number;
}

const initialState: StoreState = {
  data: {},
  connection: { status: 'idle', attempts: 0, lastMessageAt: null, lastError: null },
  clockOffsetMs: 0,
  events: [],
  lastSeq: 0,
};

/**
 * Il meteo vero (OpenWeather) non viene coperto da quello simulato del server (`source: 'mock'`).
 * Tra due meteo veri vince il più recente.
 */
function mergeWeather(
  current: WeatherSlice | undefined,
  incoming: WeatherSlice | undefined,
): WeatherSlice | undefined {
  if (!incoming) return current;
  if (!current || current.source === 'mock') return incoming;
  if (incoming.source === 'mock') return current;
  return incoming.updatedAt >= current.updatedAt ? incoming : current;
}

/** Sostituisce i dati tenendo il meteo vero già presente. */
function withKeptWeather(
  current: Partial<DisplayState>,
  incoming: Partial<DisplayState>,
): Partial<DisplayState> {
  const weather = mergeWeather(current.weather, incoming.weather);
  return weather ? { ...incoming, weather } : { ...incoming };
}

/** Reducer puro: applica un messaggio del server allo stato. Testabile senza socket. */
export function applyServerMessage(state: StoreState, msg: ServerMessage): StoreState {
  const now = Date.now();
  const base: StoreState = {
    ...state,
    lastSeq: msg.seq ?? state.lastSeq,
    connection: { ...state.connection, lastMessageAt: now },
  };

  switch (msg.type) {
    case 'welcome':
      return {
        ...base,
        clockOffsetMs: msg.payload.serverTime - now,
        connection: { ...base.connection, status: 'live', attempts: 0, lastError: null },
      };

    case 'snapshot':
      return { ...base, data: withKeptWeather(state.data, msg.payload.state) };

    case 'update':
      return {
        ...base,
        data:
          msg.payload.domain === 'weather'
            ? { ...base.data, weather: mergeWeather(base.data.weather, msg.payload.data) }
            : { ...base.data, [msg.payload.domain]: msg.payload.data },
      };

    case 'series.append': {
      const energy = base.data.energy;
      if (!energy || energy.series.key !== msg.payload.key) return base;
      // Unisce per timestamp: un punto con lo stesso bucket sostituisce quello esistente.
      const byT = new Map(energy.series.points.map((p) => [p[0], p] as const));
      for (const p of msg.payload.points) byT.set(p[0], p);
      const points = [...byT.values()].sort((a, b) => a[0] - b[0]);
      return {
        ...base,
        data: {
          ...base.data,
          energy: { ...energy, ...msg.payload.patch, series: { ...energy.series, points } },
        },
      };
    }

    case 'event':
      return { ...base, events: [msg.payload, ...base.events].slice(0, 20) };

    case 'pong':
      // Media mobile per non far saltare l'orologio a ogni pong.
      return {
        ...base,
        clockOffsetMs: Math.round(base.clockOffsetMs * 0.8 + (msg.payload.serverTime - now) * 0.2),
      };

    case 'error':
      return {
        ...base,
        connection: {
          ...base.connection,
          lastError: `${msg.payload.code}: ${msg.payload.message}`,
        },
      };

    case 'command':
    case 'ack':
      return base; // gestiti dal socket, non modificano lo stato
  }
}

type Listener = () => void;

/** Store minimale compatibile con useSyncExternalStore (nessuna dipendenza esterna). */
export class DisplayStore {
  private state: StoreState = initialState;
  private listeners = new Set<Listener>();

  getState = (): StoreState => this.state;

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  dispatch(msg: ServerMessage): void {
    this.set(applyServerMessage(this.state, msg));
  }

  setConnection(patch: Partial<StoreState['connection']>): void {
    this.set({ ...this.state, connection: { ...this.state.connection, ...patch } });
  }

  /**
   * Carica lo stato salvato sul dispositivo (app riaperta senza rete). Non tocca la connessione:
   * i dati sono vecchi per i loro `updatedAt` e per `lastMessageAt`, quindi i pannelli li dichiarano tali.
   * Non fa nulla se sono già arrivati dati dal server.
   */
  hydrate(data: Partial<DisplayState>, savedAt: number): boolean {
    if (Object.keys(this.state.data).length > 0 || Object.keys(data).length === 0) return false;
    this.set({
      ...this.state,
      data,
      connection: { ...this.state.connection, lastMessageAt: savedAt },
    });
    return true;
  }

  /** Usato dal fallback REST. */
  replaceData(data: Partial<DisplayState>, serverTime: number): void {
    this.set({
      ...this.state,
      data: withKeptWeather(this.state.data, data),
      clockOffsetMs: serverTime - Date.now(),
    });
  }

  /** Meteo letto dall'app stessa (OpenWeather): non è un messaggio del server, la connessione non cambia. */
  setWeather(weather: WeatherSlice): void {
    this.set({ ...this.state, data: { ...this.state.data, weather } });
  }

  /** Riporta lo stato iniziale (dopo un lungo sonno o una nuova connessione). */
  reset(): void {
    this.set(initialState);
  }

  private set(next: StoreState): void {
    this.state = next;
    this.listeners.forEach((l) => l());
  }
}
